import {
    HttpNetworkError,
    type Errors,
    type RequestPayload,
    type VisitOptions,
} from '@inertiajs/core';
import { router } from '@inertiajs/vue3';
import { trans } from 'laravel-vue-i18n';
import { ref } from 'vue';

type MutationUrl = Parameters<typeof router.delete>[0];

/**
 * Options de `router.post/put/patch/delete`. `onError` peut renvoyer `false`
 * quand l'appelant affiche lui-même les erreurs de validation (pas de toast).
 * `preserveState` vaut `true` par défaut (voir `useMutation`) ; passer
 * `preserveState: false` pour remonter la page.
 */
export type MutationOptions = Omit<
    VisitOptions,
    'method' | 'data' | 'onError' | 'onFinish'
> & {
    data?: RequestPayload;
    onFinish?: (
        visit: Parameters<NonNullable<VisitOptions['onBefore']>>[0],
    ) => void;
    onError?: (errors: Errors) => boolean | void;
};

/** Toast d'erreur via le flash client Inertia, lu par ToastNotifications. */
export function showErrorToast(message: string): void {
    router.flash('toast', { type: 'error', message });
}

/** Toast d'information côté client (ex. action lancée, en attente du serveur). */
export function showInfoToast(message: string): void {
    router.flash('toast', { type: 'info', message });
}

/** Premier message d'erreur de validation (422) renvoyé par le serveur. */
export function firstErrorMessage(errors: Errors): string | undefined {
    for (const value of Object.values(errors)) {
        const message = Array.isArray(value) ? value[0] : value;
        if (message) return message;
    }
    return undefined;
}

/**
 * Mutation Inertia avec état `processing`, envoi unique et retour d'erreur.
 *
 * - un appel pendant `processing` est ignoré (renvoie `false`) ;
 * - 422 : toast du premier message, sauf si `onError` renvoie `false` ;
 * - réponse non Inertia (≥ 400) : toast générique, sans la modale HTML
 *   d'Inertia (gardée en développement pour le débogage). Les 403/404/500
 *   Inertia affichent déjà ErrorPage : pas de toast ;
 * - erreur réseau (`HttpNetworkError`) : toast générique. Toute autre
 *   exception (callback qui lève…) est laissée à Inertia, qui la rejette ;
 * - les callbacks de l'appelant sont appelés, `processing` repasse à `false`
 *   avant son `onFinish` ;
 * - `preserveState: true` par défaut, comme `router.post/put/patch/delete` :
 *   `router.visit` part de `false` et remonterait la page (focus sur `body`,
 *   modale, sélection et saisie perdues). Sur une redirection vers une autre
 *   page, Inertia ne conserve rien ; `preserveState: false` remonte la page.
 */
export function useMutation() {
    const processing = ref(false);

    function send(
        method: 'post' | 'put' | 'patch' | 'delete',
        url: MutationUrl,
        options: MutationOptions = {},
    ): boolean {
        if (processing.value) return false;
        processing.value = true;

        const {
            onBefore,
            onError,
            onHttpException,
            onNetworkError,
            onFinish,
            ...rest
        } = options;

        let finished = false;
        const finish = (
            visit: Parameters<NonNullable<VisitOptions['onBefore']>>[0],
        ) => {
            if (finished) return;
            finished = true;
            processing.value = false;
            onFinish?.(visit);
        };

        router.visit(url, {
            ...rest,
            preserveState: rest.preserveState ?? true,
            method,
            onBefore: (visit) => {
                const result = onBefore?.(visit);
                if (result === false) {
                    visit.cancelled = true;
                    finish(visit);
                }
                return result;
            },
            onError: (errors) => {
                if (onError?.(errors) === false) return;
                showErrorToast(
                    firstErrorMessage(errors) ??
                        trans('common.errors.mutation_failed'),
                );
            },
            onHttpException: (response) => {
                const result = onHttpException?.(response);
                if (result === false) return false;
                if (!response.headers?.['x-inertia']) {
                    showErrorToast(trans('common.errors.mutation_failed'));
                    // Un seul retour : pas de modale HTML en plus du toast.
                    return import.meta.env?.DEV ? result : false;
                }
                return result;
            },
            onNetworkError: (error) => {
                if (onNetworkError?.(error) === false) return false;
                // Exception d'un callback : Inertia la rejette (console).
                if (!(error instanceof HttpNetworkError)) return;
                showErrorToast(trans('common.errors.network'));
                // Erreur traitée : pas de rejet de promesse non capturé.
                return false;
            },
            onFinish: finish,
        });

        return true;
    }

    return {
        processing,
        post: (
            url: MutationUrl,
            data?: RequestPayload,
            options?: MutationOptions,
        ) => send('post', url, { ...options, data }),
        put: (
            url: MutationUrl,
            data?: RequestPayload,
            options?: MutationOptions,
        ) => send('put', url, { ...options, data }),
        patch: (
            url: MutationUrl,
            data?: RequestPayload,
            options?: MutationOptions,
        ) => send('patch', url, { ...options, data }),
        delete: (url: MutationUrl, options?: MutationOptions) =>
            send('delete', url, options),
    };
}
